import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Button, Input, Select } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDate, formatHours, formatMoney, formatMonth } from '../../lib/format';
import type { UnbilledEntry } from './api';

/**
 * Shared invoice line-item editor — used by both the New Invoice page
 * (manual + from-unbilled paths) and the Draft editor.
 */

export const KIND_OPTIONS = [
  { value: 'service', label: 'Service' },
  { value: 'support', label: 'Support' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'hosting', label: 'Hosting' },
  { value: 'fee', label: 'Fee' },
  { value: 'other', label: 'Other' },
];

/**
 * The dropdown options for a line, including its own kind when that is a
 * Harvest-era category ("(m) support") — shown as itself, never quietly as
 * "Service", which is what an unknown value used to look like.
 */
export function kindOptionsFor(kind: string, base = KIND_OPTIONS): Array<{ value: string; label: string }> {
  if (!kind || base.some((o) => o.value === kind)) return base;
  return [{ value: kind, label: `${harvestKindLabel(kind)} (from Harvest)` }, ...base];
}

/** "(m) website maintenance program" → "(M) Website Maintenance Program". */
export function harvestKindLabel(kind: string): string {
  return kind.replace(/\(([a-z])\)/g, (_, c: string) => `(${c.toUpperCase()})`).replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

export interface EditableLine {
  key: string;
  description: string;
  detail?: string;
  agreementId: string | null;
  quantity: string;
  unitPrice: string;
  kind: string;
  timeEntryIds: string[];
  expenseIds: string[];
  source: 'time' | 'expense' | 'manual';
  /** Time entries behind a rolled-up line — reviewable and excludable. */
  entries?: UnbilledEntry[];
}

let lineSeq = 0;
export function newLineKey(): string {
  return `line-${++lineSeq}`;
}

export function emptyLine(): EditableLine {
  return {
    key: newLineKey(),
    description: '',
    agreementId: null,
    quantity: '1',
    unitPrice: '',
    kind: 'service',
    timeEntryIds: [],
    expenseIds: [],
    source: 'manual',
  };
}

/**
 * Numeric cell input: right-aligned tabular figures with the browser's
 * number spinners suppressed — they steal ~16px and clip values like
 * "175.50" inside a narrow qty cell.
 */
const NUM_INPUT_CLASS = [
  'num',
  '[&_input]:text-right',
  '[&_input]:px-2.5',
  '[&_input]:[appearance:textfield]',
  '[&_input::-webkit-outer-spin-button]:appearance-none',
  '[&_input::-webkit-inner-spin-button]:appearance-none',
  '[&_input::-webkit-outer-spin-button]:m-0',
  '[&_input::-webkit-inner-spin-button]:m-0',
].join(' ');

/**
 * Money is two decimal places, in the input as well as in the column that
 * sums it. A rate field reading "312.5" next to a strictly 2-dp AMOUNT column
 * is the same figure written two ways on one row; normalising on blur keeps
 * the editor's discipline without fighting the user mid-keystroke.
 */
export function normalizeMoneyInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const value = Number(trimmed);
  return Number.isFinite(value) ? value.toFixed(2) : raw;
}

/**
 * The description is the sentence you are about to bill a client for, so it
 * has to be readable in full. A single-line input truncated it mid-string
 * ("…— Jan 6 – Aug 4") with no way to see the rest; this grows to fit
 * instead, and still looks like every other field in the row.
 */
function DescriptionField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      aria-label="Line description"
      placeholder="What was this work?"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full resize-none overflow-hidden rounded-lg border border-grey-200 bg-white px-3 py-1.5 text-sm leading-5 text-secondary transition-all duration-200 placeholder:text-grey-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
    />
  );
}

export function lineAmount(line: EditableLine): number {
  const qty = Number(line.quantity);
  const rate = Number(line.unitPrice);
  if (!Number.isFinite(qty) || !Number.isFinite(rate)) return 0;
  return Math.round(qty * rate * 100) / 100;
}

export function linesSubtotal(lines: EditableLine[]): number {
  return Math.round(lines.reduce((s, l) => s + lineAmount(l), 0) * 100) / 100;
}

export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function isoAddDays(iso: string, days: number): string {
  const date = new Date(`${iso}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Time-entry drill-down (review / exclude before billing)
// ---------------------------------------------------------------------------

function EntryDrilldown({
  line,
  onChange,
}: {
  line: EditableLine;
  onChange: (patch: Partial<EditableLine>) => void;
}) {
  const [open, setOpen] = useState(false);
  const entries = line.entries ?? [];
  const included = new Set(line.timeEntryIds);
  const excluded = entries.length - included.size;

  const toggle = (entry: UnbilledEntry) => {
    const next = new Set(included);
    if (next.has(entry.id)) next.delete(entry.id);
    else next.add(entry.id);
    // Quantity follows the included entries so the line total never lies.
    const hours = Math.round(entries.filter((e) => next.has(e.id)).reduce((s, e) => s + e.hours, 0) * 100) / 100;
    onChange({
      timeEntryIds: entries.filter((e) => next.has(e.id)).map((e) => e.id),
      quantity: String(hours),
      // Keep the generated "… — 14.25 hrs @ $175.00/hr — October 2025" copy
      // honest when entries are excluded (left alone if the user rewrote it).
      description: line.description.replace(
        /(—\s*)[\d,.]+(\s*hrs\s*@)/,
        `$1${formatHours(hours)}$2`,
      ),
    });
  };

  return (
    <div className="mt-0.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded text-caption font-semibold text-accent-700 transition-colors hover:text-accent-800 hover:underline"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden />
        )}
        Bills {included.size} time entr{included.size === 1 ? 'y' : 'ies'}
        {excluded > 0 && <span className="text-grey-500">· {excluded} excluded</span>}
      </button>
      {open && (
        <div className="mt-1.5 max-h-64 overflow-y-auto rounded-lg border border-grey-200 bg-grey-50">
          <table className="w-full border-collapse text-caption">
            <thead>
              <tr className="text-left text-grey-500">
                <th className="w-8 px-2 py-1.5" aria-label="Include" />
                <th className="px-2 py-1.5 font-bold uppercase tracking-wider">Date</th>
                <th className="px-2 py-1.5 font-bold uppercase tracking-wider">Who</th>
                <th className="px-2 py-1.5 font-bold uppercase tracking-wider">Notes</th>
                <th className="px-2 py-1.5 text-right font-bold uppercase tracking-wider">Hrs</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const on = included.has(e.id);
                return (
                  <tr
                    key={e.id}
                    className={cn('border-t border-grey-200 bg-white', !on && 'opacity-50')}
                  >
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(e)}
                        aria-label={`Bill ${e.hours} hours on ${e.date} by ${e.personName}`}
                        className="h-3.5 w-3.5 cursor-pointer accent-accent-600"
                      />
                    </td>
                    <td className="num whitespace-nowrap px-2 py-1.5 text-grey-600">
                      {formatDate(e.date)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-secondary">{e.personName}</td>
                    <td className="max-w-[18rem] truncate px-2 py-1.5 text-grey-500">
                      {e.notes || e.taskName || '—'}
                    </td>
                    <td className="num-cell px-2 py-1.5 font-semibold">{formatHours(e.hours)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line items editor (live qty × rate math)
// ---------------------------------------------------------------------------

export function LineItemsEditor({
  lines,
  onChange,
  allowAdd,
  meta,
  invalidKeys,
}: {
  lines: EditableLine[];
  onChange: (lines: EditableLine[]) => void;
  allowAdd: boolean;
  /**
   * ⚠ THE OFFENDING CELL IS MARKED, not merely described.
   *
   * "Line amounts cannot be negative." used to render at the bottom of the
   * card with nothing on screen saying WHICH line — on a twelve-line invoice
   * that is a hunt. Keys named here wear a red rail and a red number pair, and
   * the page scrolls the first one into view.
   */
  invalidKeys?: Set<string>;
  /**
   * Drives the totals foot. Subtotal/Tax/Total belong in the same table as the
   * amounts they sum — stacked under the AMOUNT column, the way every ledger
   * and every invoice does it. Rendering them in a separate block beside the
   * table put the sum a column and a half away from its addends.
   */
  meta: InvoiceMeta;
}) {
  const update = (key: string, patch: Partial<EditableLine>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key: string) => onChange(lines.filter((l) => l.key !== key));
  const subtotal = linesSubtotal(lines);
  const discount = metaDiscount(meta, subtotal);
  // Tax is owed on what the client actually pays, so it follows the discount.
  const tax = metaTax(meta, subtotal - discount);

  /** Shared by the table row and the mobile card — one editor, two layouts. */
  const qtyInput = (line: EditableLine) => (
    <Input
      aria-label="Quantity"
      size="sm"
      type="number"
      inputMode="decimal"
      min="0"
      step="0.25"
      className={NUM_INPUT_CLASS}
      value={line.quantity}
      onChange={(e) => update(line.key, { quantity: e.target.value })}
    />
  );
  const rateInput = (line: EditableLine) => (
    <Input
      aria-label="Unit price"
      size="sm"
      type="number"
      inputMode="decimal"
      min="0"
      step="0.01"
      placeholder="0.00"
      className={NUM_INPUT_CLASS}
      value={line.unitPrice}
      onChange={(e) => update(line.key, { unitPrice: e.target.value })}
      // Money settles to two decimals the moment you leave the field, so the
      // rate reads like the amount it multiplies.
      onBlur={(e) => update(line.key, { unitPrice: normalizeMoneyInput(e.target.value) })}
    />
  );
  const provenance = (line: EditableLine) => (
    <>
      {line.detail && <p className="mt-1 text-caption text-grey-500">{line.detail}</p>}
      {line.source === 'time' &&
        (line.entries && line.entries.length > 0 ? (
          <EntryDrilldown line={line} onChange={(patch) => update(line.key, patch)} />
        ) : (
          <p className="mt-0.5 text-caption text-accent-700">
            Bills {line.timeEntryIds.length} time entr
            {line.timeEntryIds.length === 1 ? 'y' : 'ies'}
          </p>
        ))}
      {line.source === 'expense' && (
        <p className="mt-0.5 text-caption text-accent-700">
          Bills {line.expenseIds.length} expense{line.expenseIds.length === 1 ? '' : 's'}
        </p>
      )}
    </>
  );

  return (
    <>
      {/* ⚠ THE PHONE LAYOUT IS NOT A SCROLLER. This grid is the only place in
          the app where you type money, and at 390px its 640px table put the
          Rate input at x=376 and the Amount at x=364–490 — both outside the
          viewport, with no scrollbar to say so. You could tap "Create draft
          invoice" having never seen the rate you were billing at. Everything
          else in this domain already becomes cards below `md`; this was the one
          unconverted component. */}
      <div className="space-y-3 md:hidden">
        {lines.map((line, index) => (
          <div
            key={line.key}
            data-line-key={line.key}
            className={cn(
              'rounded-xl border bg-white p-3 shadow-sm',
              invalidKeys?.has(line.key)
                ? 'border-status-late bg-status-late-bg/60'
                : 'border-grey-200',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="num shrink-0 text-caption font-bold uppercase tracking-wider text-grey-400">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <Select
                  aria-label="Line kind"
                  selectSize="sm"
                  value={line.kind}
                  onChange={(e) => update(line.key, { kind: e.target.value })}
                  options={kindOptionsFor(line.kind)}
                />
              </div>
              <button
                type="button"
                aria-label="Remove line"
                onClick={() => remove(line.key)}
                className="shrink-0 rounded-lg p-1.5 text-grey-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="mt-2">
              <DescriptionField
                value={line.description}
                onChange={(description) => update(line.key, { description })}
              />
              {provenance(line)}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="block">
                <span className="block text-caption font-bold uppercase tracking-wider text-grey-500">
                  Qty
                </span>
                {qtyInput(line)}
              </label>
              <label className="block">
                <span className="block text-caption font-bold uppercase tracking-wider text-grey-500">
                  Rate
                </span>
                {rateInput(line)}
              </label>
            </div>
            {/* The number the two fields above produce, on its own line and
                never off-screen. */}
            <div className="mt-2 flex items-baseline justify-between border-t border-grey-100 pt-2">
              <span className="text-caption font-bold uppercase tracking-wider text-grey-500">
                Amount
              </span>
              <span className="num-cell text-body font-bold text-secondary">
                {formatMoney(lineAmount(line))}
              </span>
            </div>
          </div>
        ))}
        {allowAdd && (
          <Button size="sm" variant="ghost" onClick={() => onChange([...lines, emptyLine()])}>
            <Plus className="h-4 w-4" aria-hidden />
            Add line
          </Button>
        )}
        <dl className="num rounded-xl border border-grey-200 bg-grey-50 p-3">
          <div className="flex justify-between py-0.5">
            <dt className="text-body-sm text-grey-500">Subtotal</dt>
            <dd className="num-cell text-body-sm font-medium">{formatMoney(subtotal)}</dd>
          </div>
          {discount > 0 && (
            <div className="flex justify-between py-0.5">
              <dt className="text-body-sm text-grey-500">
                Discount
                {meta.discountMode === 'rate' && Number(meta.discountInput) > 0 && (
                  <span className="ml-1 text-grey-400">({Number(meta.discountInput)}%)</span>
                )}
              </dt>
              <dd className="num-cell text-body-sm font-medium text-success">
                ({formatMoney(discount)})
              </dd>
            </div>
          )}
          {tax > 0 && (
            <div className="flex justify-between py-0.5">
              <dt className="text-body-sm text-grey-500">
                Tax
                {meta.taxMode === 'rate' && Number(meta.taxInput) > 0 && (
                  <span className="ml-1 text-grey-400">({Number(meta.taxInput)}%)</span>
                )}
              </dt>
              <dd className="num-cell text-body-sm font-medium">{formatMoney(tax)}</dd>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t border-secondary pt-2">
            <dt className="text-base font-bold text-secondary">Total</dt>
            <dd className="num-cell text-base font-bold text-secondary">
              {formatMoney(subtotal - discount + tax)}
            </dd>
          </div>
        </dl>
      </div>

      <div className="hidden overflow-x-auto md:block">
      <table className="w-full min-w-[640px] border-collapse text-body-sm">
        <thead>
          <tr className="border-b border-grey-200 bg-grey-50 text-left text-caption font-bold uppercase tracking-wider text-grey-500">
            <th className="px-3 py-2.5 w-32">Kind</th>
            <th className="px-3 py-2.5">Description</th>
            <th className="px-3 py-2.5 w-28 text-right">Qty</th>
            <th className="px-3 py-2.5 w-32 text-right">Rate</th>
            <th className="px-3 py-2.5 w-32 text-right">Amount</th>
            <th className="w-10" aria-label="Remove" />
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr
              key={line.key}
              data-line-key={line.key}
              className={cn(
                'border-b border-grey-100 align-top',
                // The row that stopped the save, marked where it happened.
                invalidKeys?.has(line.key) && 'bg-status-late-bg/60 outline outline-1 outline-status-late/40',
              )}
            >
              <td className="px-3 py-2">
                <Select
                  aria-label="Line kind"
                  selectSize="sm"
                  value={line.kind}
                  onChange={(e) => update(line.key, { kind: e.target.value })}
                  options={kindOptionsFor(line.kind)}
                />
              </td>
              <td className="px-3 py-2">
                <DescriptionField
                  value={line.description}
                  onChange={(description) => update(line.key, { description })}
                />
                {provenance(line)}
              </td>
              <td className="px-3 py-2">{qtyInput(line)}</td>
              <td className="px-3 py-2">{rateInput(line)}</td>
              <td
                className={cn(
                  'num-cell px-3 py-3.5 font-semibold',
                  invalidKeys?.has(line.key) ? 'text-status-late' : 'text-secondary',
                )}
              >
                {formatMoney(lineAmount(line))}
              </td>
              <td className="py-2 pr-2">
                <button
                  type="button"
                  aria-label="Remove line"
                  onClick={() => remove(line.key)}
                  className="rounded-lg p-1.5 text-grey-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </td>
            </tr>
          ))}
          {allowAdd && (
            <tr>
              <td colSpan={6} className="px-3 py-2">
                <Button size="sm" variant="ghost" onClick={() => onChange([...lines, emptyLine()])}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Add line
                </Button>
              </td>
            </tr>
          )}
        </tbody>
        {/* Totals live in the table's own foot so they sit directly under the
            AMOUNT column they sum — alignment by construction, not by luck. */}
        <tfoot className="num">
          <tr>
            <td colSpan={3} className="px-3 pt-3" />
            <td className="px-3 pt-3 text-right text-grey-500">Subtotal</td>
            <td className="num-cell px-3 pt-3 font-medium">{formatMoney(subtotal)}</td>
            <td />
          </tr>
          {discount > 0 && (
            <tr>
              <td colSpan={3} />
              <td className="whitespace-nowrap px-3 py-1 text-right text-grey-500">
                Discount
                {meta.discountMode === 'rate' && Number(meta.discountInput) > 0 && (
                  <span className="ml-1 text-grey-400">({Number(meta.discountInput)}%)</span>
                )}
              </td>
              {/* A reduction is written the way money leaving is written
                  everywhere else in this app: in parentheses, in green. */}
              <td className="num-cell px-3 py-1 font-medium text-success">
                ({formatMoney(discount)})
              </td>
              <td />
            </tr>
          )}
          {tax > 0 && (
            <tr>
              <td colSpan={3} />
              <td className="whitespace-nowrap px-3 py-1 text-right text-grey-500">
                Tax
                {meta.taxMode === 'rate' && Number(meta.taxInput) > 0 && (
                  <span className="ml-1 text-grey-400">({Number(meta.taxInput)}%)</span>
                )}
              </td>
              <td className="num-cell px-3 py-1 font-medium">{formatMoney(tax)}</td>
              <td />
            </tr>
          )}
          <tr>
            <td colSpan={3} />
            <td className="border-t border-secondary px-3 py-2 text-right text-base font-bold text-secondary">
              Total
            </td>
            <td className="num-cell border-t border-secondary px-3 py-2 text-base font-bold text-secondary">
              {formatMoney(subtotal - discount + tax)}
            </td>
            {/* The rule stops at the amount column — it underlines the sum,
                not the row-actions gutter. */}
            <td />
          </tr>
        </tfoot>
      </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Invoice meta fields (dates, terms, tax, notes)
// ---------------------------------------------------------------------------

export interface InvoiceMeta {
  issueDate: string;
  dueDate: string;
  /** Client-facing "what is this for?" line — the list column is CLIENT / SUBJECT. */
  subject: string;
  /** The client's purchase-order number; AP often will not pay without it. */
  poNumber: string;
  notes: string;
  /**
   * A reduction off the subtotal, entered as a rate (%) or a flat amount ($),
   * and applied BEFORE tax. Harvest has had this field for years; without it
   * "10% off, as quoted" had to be typed as a negative line item that no
   * report could recognise as a discount.
   */
  discountMode: 'rate' | 'amount';
  discountInput: string;
  /** Tax entered as a rate (%) or a flat amount ($). */
  taxMode: 'rate' | 'amount';
  taxInput: string;
  /** Net payment terms in days; null = the due date was set by hand. */
  termsDays: number | null;
}

export const TERMS_OPTIONS = [
  { value: '0', label: 'Due on receipt' },
  { value: '15', label: 'Net 15' },
  { value: '30', label: 'Net 30' },
  { value: '45', label: 'Net 45' },
  { value: '60', label: 'Net 60' },
  { value: 'custom', label: 'Custom date' },
];

/**
 * Tax in dollars. `base` is the subtotal AFTER any discount — tax is owed on
 * what the client actually pays, which is also the order the server computes
 * the total in.
 */
export function metaTax(meta: InvoiceMeta, base: number): number {
  const value = Number(meta.taxInput);
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (meta.taxMode === 'amount') return Math.round(value * 100) / 100;
  return Math.round(base * value) / 100;
}

/**
 * The discount in dollars, clamped to the subtotal: an invoice cannot owe the
 * client money — that is a credit note, and the server rejects it too.
 */
export function metaDiscount(meta: InvoiceMeta, subtotal: number): number {
  const value = Number(meta.discountInput);
  if (!Number.isFinite(value) || value <= 0) return 0;
  const dollars =
    meta.discountMode === 'amount'
      ? Math.round(value * 100) / 100
      : Math.round(subtotal * value) / 100;
  return Math.min(dollars, Math.max(0, subtotal));
}

export function defaultMeta(issueDate = todayIso(), termsDays = 30): InvoiceMeta {
  return {
    issueDate,
    dueDate: isoAddDays(issueDate, termsDays),
    subject: '',
    poNumber: '',
    notes: '',
    discountMode: 'rate',
    discountInput: '',
    taxMode: 'rate',
    taxInput: '',
    termsDays,
  };
}

/** Terms label for a saved invoice ("Net 30" / "Custom date"). */
function termsFromDates(issueDate: string, dueDate: string): number | null {
  const days = Math.round(
    (new Date(`${dueDate}T12:00:00Z`).getTime() - new Date(`${issueDate}T12:00:00Z`).getTime()) /
      86_400_000,
  );
  return TERMS_OPTIONS.some((o) => o.value === String(days)) ? days : null;
}

/** Seed the meta editor from an existing invoice. */
export function metaFromInvoice(inv: {
  issueDate: string;
  dueDate: string;
  notes: string | null;
  subject?: string | null;
  poNumber?: string | null;
  discountAmount?: number | null;
  taxAmount: number;
}): InvoiceMeta {
  const discount = inv.discountAmount ?? 0;
  return {
    issueDate: inv.issueDate,
    dueDate: inv.dueDate,
    subject: inv.subject ?? '',
    poNumber: inv.poNumber ?? '',
    notes: inv.notes ?? '',
    // A saved invoice carries dollars, not the rate they came from — reopening
    // it in % mode would re-derive a different figure from a changed subtotal.
    discountMode: 'amount',
    discountInput: discount > 0 ? discount.toFixed(2) : '',
    taxMode: 'amount',
    taxInput: inv.taxAmount > 0 ? inv.taxAmount.toFixed(2) : '',
    termsDays: termsFromDates(inv.issueDate, inv.dueDate),
  };
}

/**
 * A money field that can be typed either way — "10%" or "$250" — with the
 * dollars it works out to spelled out underneath.
 *
 * Discount and Tax are the same control because they are the same idea (a
 * percentage of the subtotal, or a flat figure), and shipping them as two
 * hand-rolled blocks is how a "0.000" placeholder ends up beside a "0.00" one.
 */
function RateOrAmountField({
  label,
  mode,
  value,
  base,
  baseLabel,
  dollars,
  rateHint,
  amountHint,
  onChange,
}: {
  label: string;
  mode: 'rate' | 'amount';
  value: string;
  /** What a percentage is a percentage OF. */
  base: number;
  baseLabel: string;
  dollars: number;
  rateHint: string;
  amountHint: string;
  onChange: (patch: { mode: 'rate' | 'amount'; value: string }) => void;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="label mb-0">{label}</span>
        <div
          role="group"
          aria-label={`${label} input mode`}
          className="flex items-center gap-0.5 rounded-md border border-grey-200 p-0.5"
        >
          {(['rate', 'amount'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              // Clear on switch: a dollar figure read as a percent (or vice
              // versa) would silently produce a wildly wrong figure.
              onClick={() => onChange({ mode: option, value: mode === option ? value : '' })}
              className={cn(
                'rounded px-1.5 py-0.5 text-caption font-bold transition-colors',
                mode === option ? 'bg-secondary text-white' : 'text-grey-500 hover:text-secondary',
              )}
            >
              {option === 'rate' ? '%' : '$'}
            </button>
          ))}
        </div>
      </div>
      <Input
        aria-label={mode === 'rate' ? `${label} rate percent` : `${label} amount`}
        type="number"
        inputMode="decimal"
        min="0"
        step={mode === 'rate' ? '0.001' : '0.01'}
        // Two decimals in both modes: "0.000" beside "0.00" made the same
        // field look like two different kinds of number. Fractional rates
        // (8.375%) still type fine — the step allows them.
        placeholder="0.00"
        className="num [&_input]:text-right"
        value={value}
        onChange={(e) => onChange({ mode, value: e.target.value })}
        onBlur={(e) =>
          onChange({ mode, value: mode === 'amount' ? normalizeMoneyInput(e.target.value) : e.target.value })
        }
      />
      <p className="mt-1 text-caption text-grey-500">
        {mode === 'rate' ? (
          Number(value) > 0 ? (
            <>
              {Number(value)}% of {formatMoney(base)} {baseLabel} ={' '}
              <span className="num font-semibold text-secondary">{formatMoney(dollars)}</span>
            </>
          ) : (
            rateHint
          )
        ) : (
          amountHint
        )}
      </p>
    </div>
  );
}

/**
 * A subject suggestion taken from the work actually on the invoice, not from a
 * hardcoded example. Billing Halcyon's Booking Engine used to be offered
 * "Support & maintenance — August 2026", which is the wrong sentence for a
 * service agreement and quietly teaches people to ignore the field.
 */
export function suggestSubject(lines: EditableLine[], issueDate: string): string {
  const month = issueDate ? formatMonth(issueDate) : '';
  const suffix = month ? ` — ${month}` : '';
  // Generated descriptions read "Booking Engine — 14.25 hrs @ $175.00/hr —
  // October 2025"; the head of that is the thing being billed.
  const head = lines
    .map((l) => l.description.split(' — ')[0].trim())
    .find((d) => d.length > 0);
  if (head) return `${head}${suffix}`;
  const kinds = new Set(lines.map((l) => l.kind));
  const kind = kinds.size === 1 ? [...kinds][0] : 'service';
  const label = KIND_OPTIONS.find((k) => k.value === kind)?.label ?? 'Professional services';
  return `${label}${suffix}`;
}

export function MetaFields({
  meta,
  onChange,
  subtotal,
  lines = [],
}: {
  meta: InvoiceMeta;
  onChange: (m: InvoiceMeta) => void;
  /** Used to preview the tax dollars behind a rate. */
  subtotal: number;
  /** The invoice's own lines — the subject suggestion is taken from them. */
  lines?: EditableLine[];
}) {
  const discountDollars = metaDiscount(meta, subtotal);
  const taxable = Math.round((subtotal - discountDollars) * 100) / 100;
  const taxDollars = metaTax(meta, taxable);
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Input
        label="Subject"
        placeholder={suggestSubject(lines, meta.issueDate)}
        className="sm:col-span-2"
        maxLength={200}
        value={meta.subject}
        onChange={(e) => onChange({ ...meta, subject: e.target.value })}
        // The placeholder is saved verbatim when this is left blank — see the
        // note at the `subject:` line in NewInvoicePage/EditInvoicePage.
        hint="Left blank, the suggestion above is saved. Shown on the invoice and in the CLIENT / SUBJECT column."
      />
      <Input
        label="PO number"
        placeholder="PO-2026-0142"
        maxLength={60}
        value={meta.poNumber}
        onChange={(e) => onChange({ ...meta, poNumber: e.target.value })}
        hint="Printed on the invoice when the client issues one"
      />
      <Input
        label="Issue date"
        type="date"
        value={meta.issueDate}
        onChange={(e) => {
          const issueDate = e.target.value;
          onChange({
            ...meta,
            issueDate,
            // Keep the terms promise: Net 30 stays 30 days from the new issue date.
            dueDate:
              meta.termsDays !== null && issueDate
                ? isoAddDays(issueDate, meta.termsDays)
                : meta.dueDate,
          });
        }}
      />
      <Select
        label="Payment terms"
        value={meta.termsDays === null ? 'custom' : String(meta.termsDays)}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'custom') {
            onChange({ ...meta, termsDays: null });
            return;
          }
          const days = Number(v);
          onChange({ ...meta, termsDays: days, dueDate: isoAddDays(meta.issueDate, days) });
        }}
        options={TERMS_OPTIONS}
      />
      <Input
        label="Due date"
        type="date"
        min={meta.issueDate}
        value={meta.dueDate}
        onChange={(e) => onChange({ ...meta, dueDate: e.target.value, termsDays: null })}
      />
      <RateOrAmountField
        label="Discount"
        mode={meta.discountMode}
        value={meta.discountInput}
        base={subtotal}
        baseLabel="subtotal"
        dollars={discountDollars}
        rateHint="Taken off the subtotal before tax — as quoted."
        amountHint="Flat reduction off the subtotal, before tax."
        onChange={({ mode, value }) =>
          onChange({ ...meta, discountMode: mode, discountInput: value })
        }
      />
      <RateOrAmountField
        label="Tax"
        mode={meta.taxMode}
        value={meta.taxInput}
        base={taxable}
        baseLabel={discountDollars > 0 ? 'after discount' : 'subtotal'}
        dollars={taxDollars}
        rateHint="Rate applied to the subtotal — no arithmetic needed."
        amountHint="Flat tax amount in dollars."
        onChange={({ mode, value }) => onChange({ ...meta, taxMode: mode, taxInput: value })}
      />
      <Input
        label="Notes"
        className="sm:col-span-2 lg:col-span-4"
        placeholder="Shown on the invoice — deposit terms, delivery milestones, thanks…"
        value={meta.notes}
        onChange={(e) => onChange({ ...meta, notes: e.target.value })}
      />
    </div>
  );
}
